import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ClipboardList,
    Sparkles,
    Users,
    Compass,
    Clock,
    ShieldCheck,
    Heart,
    ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";

const STEPS = [
    {
        n: 1,
        title: "Tell us about you",
        body:
            "Share a few details — either through our short form or a conversation with Jarvis, our AI concierge. No pressure, no cold calls.",
        icon: ClipboardList,
        callout: { icon: Clock, text: "Takes about 3 minutes" },
        accent: "from-[#0a2540] via-[#10446e] to-[#1e6fd9]",
    },
    {
        n: 2,
        title: "Jarvis pre-qualifies",
        body:
            "Jarvis understands your goals and organizes the information a licensed advisor needs — so your first real conversation isn't a discovery call.",
        icon: Sparkles,
        callout: { icon: ShieldCheck, text: "We do not sell your data" },
        accent: "from-[#10446e] via-[#1e6fd9] to-[#4ba3f5]",
    },
    {
        n: 3,
        title: "Advisor review",
        body:
            "A licensed Moving Mountains advisor reviews the fit. Jeremy and Brent personally vet every case before any recommendation is shared.",
        icon: Users,
        callout: { icon: Heart, text: "No pressure tactics — ever" },
        accent: "from-[#1e6fd9] via-[#4ba3f5] to-[#9bc7f6]",
    },
    {
        n: 4,
        title: "Personalized recommendation",
        body:
            "You book a call and receive a side-by-side explanation of what we recommend and why. You decide. No surprises, no commitments.",
        icon: Compass,
        callout: { icon: ShieldCheck, text: "Approval and rates set by carrier underwriting" },
        accent: "from-[#0a2540] via-[#1e6fd9] to-[#4ba3f5]",
    },
];

export default function HowItWorks() {
    return (
        <div data-testid="how-it-works-page">
            <SEO
                title="How it works — Moving Mountains Advisors"
                description="Four steps from curiosity to covered. Honest pre-qualification, advisor review, and a recommendation that respects your time."
            />
            <PageHeader
                eyebrow="Process"
                title="How Moving Mountains works."
                subtitle="A clean four-step flow from curiosity to covered — designed around respect for your time."
            />

            <section className="mm-section bg-white">
                <div className="mm-container">
                    <ol className="grid gap-6 md:grid-cols-2">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            const Callout = s.callout.icon;
                            return (
                                <motion.li
                                    key={s.n}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.5, delay: i * 0.12, ease: "easeOut" }}
                                    className="mm-card relative overflow-hidden p-7"
                                    data-testid={`hiw-step-${s.n}`}
                                >
                                    <div
                                        className={`pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br ${s.accent} opacity-[0.10] blur-2xl`}
                                    />
                                    <div className="relative flex items-start gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${s.accent} text-white shadow-md`}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="mm-eyebrow">Step 0{s.n}</div>
                                            <h3 className="mm-display mt-1 text-2xl text-mm-navy">{s.title}</h3>
                                        </div>
                                    </div>
                                    <p className="relative mt-4 text-sm text-mm-muted leading-relaxed">
                                        {s.body}
                                    </p>
                                    <div className="relative mt-5 inline-flex items-center gap-2 rounded-full border border-mm-line bg-mm-bg/60 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-mm-navy">
                                        <Callout size={12} /> {s.callout.text}
                                    </div>
                                </motion.li>
                            );
                        })}
                    </ol>

                    <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-mm-line bg-gradient-to-br from-mm-bg to-white p-8 sm:flex-row" data-testid="hiw-cta">
                        <div>
                            <div className="mm-eyebrow">Ready when you are</div>
                            <h3 className="mm-display mt-1 text-2xl text-mm-navy">Start with Jarvis or jump straight to a quote.</h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/jarvis" className="mm-btn-pill bg-mm-navy text-white hover:bg-mm-blue" data-testid="hiw-cta-jarvis">
                                <Sparkles size={16} /> Start With Jarvis
                            </Link>
                            <Link to="/" className="mm-btn-pill border border-mm-navy/80 bg-white text-mm-navy hover:bg-mm-navy hover:text-white" data-testid="hiw-cta-quote">
                                Get My Free Quote <ChevronRight size={16} />
                            </Link>
                        </div>
                    </div>

                    <p className="mt-10 mm-disclaimer max-w-2xl">
                        This experience is powered by advanced AI technology. A licensed
                        human advisor reviews every case before any recommendation is made.
                    </p>
                </div>
            </section>
        </div>
    );
}
